import { ChannelIcon, Empty, ErrorState, Loading, useFormat } from '@ly360/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Briefcase, CheckSquare, Handshake, MessageSquare, StickyNote } from 'lucide-react';
import { useState, type ComponentType } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { api } from '../lib/api';
import type { TimelineItem } from './data';

const ICON: Record<string, ComponentType<{ size?: number }>> = {
  note: StickyNote,
  case: Briefcase,
  deal: Handshake,
  task: CheckSquare,
  message: MessageSquare,
};
const COLOR: Record<string, string> = {
  note: '#c98a12',
  case: '#d8433c',
  deal: '#1f9d6b',
  task: '#5e8cff',
  message: 'var(--brand-1)',
};

export function Timeline({ path }: { path: string }) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const q = useQuery({ queryKey: ['timeline', path], queryFn: () => api<{ items: TimelineItem[] }>(path) });
  if (q.isLoading) return <Loading />;
  if (q.isError) return <ErrorState error={q.error} />;
  if (!q.data?.items.length) return <Empty text={t('crm.person.noTimeline')} />;
  return (
    <ol className="timeline" data-testid="timeline">
      {q.data.items.map((i) => {
        const Icon = ICON[i.kind] ?? StickyNote;
        const label =
          i.kind === 'message'
            ? t(`crm.timeline.${i.sub === 'outbound' ? 'outbound' : 'inbound'}`)
            : t(`crm.timeline.${i.kind}`);
        const href =
          i.kind === 'case'
            ? `/cases/${i.refId}`
            : i.kind === 'message'
              ? `/inbox?c=${i.refId}`
              : i.kind === 'deal'
                ? `/deals`
                : undefined;
        return (
          <li key={`${i.kind}-${i.id}`} data-kind={i.kind}>
            <span
              className="dot"
              style={{
                background: i.kind === 'message' && i.channel ? `var(--ch-${i.channel})` : COLOR[i.kind],
              }}
              aria-hidden="true"
            >
              {i.kind === 'message' && i.channel ? (
                <ChannelIcon channel={i.channel} size={12} />
              ) : (
                <Icon size={12} />
              )}
            </span>
            <div className="meta">
              <strong>{label}</strong>
              <span title={fmt.dateTimeZoned(i.at)}>{fmt.dateTime(i.at)}</span>
              {i.actor && <span>{t('crm.timeline.by', { name: i.actor })}</span>}
              {i.sub && i.kind !== 'message' && (
                <span>
                  ·{' '}
                  {t(
                    `crm.${i.kind === 'case' ? 'caseStatus' : i.kind === 'deal' ? 'dealStatus' : 'taskStatus'}.${i.sub}`,
                    { defaultValue: i.sub },
                  )}
                </span>
              )}
            </div>
            {i.title && <div className="body">{href ? <Link to={href}>{i.title}</Link> : i.title}</div>}
            {i.text && (
              <div className="body" dir="auto">
                {i.text}
              </div>
            )}
          </li>
        );
      })}
    </ol>
  );
}

export function Notes({ relatedType, relatedId }: { relatedType: string; relatedId: string }) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const qc = useQueryClient();
  const [body, setBody] = useState('');
  const q = useQuery({
    queryKey: ['notes', relatedType, relatedId],
    queryFn: () =>
      api<{ items: { id: string; body: string; createdAt: string; authorName: string | null }[] }>(
        `/v1/notes?relatedType=${relatedType}&relatedId=${relatedId}`,
      ),
  });
  const add = useMutation({
    mutationFn: () =>
      api('/v1/notes', { method: 'POST', body: JSON.stringify({ body, relatedType, relatedId }) }),
    onSuccess: () => {
      setBody('');
      void qc.invalidateQueries({ queryKey: ['notes'] });
      void qc.invalidateQueries({ queryKey: ['timeline'] });
    },
  });
  return (
    <section className="card" aria-label={t('crm.person.notes')}>
      <div className="card-head">
        <h2>{t('crm.person.notes')}</h2>
      </div>
      <form
        className="card-pad"
        onSubmit={(e) => {
          e.preventDefault();
          if (body.trim()) add.mutate();
        }}
      >
        <label htmlFor={`note-${relatedId}`} className="sr-only">
          {t('crm.person.addNote')}
        </label>
        <textarea
          id={`note-${relatedId}`}
          className="textarea"
          dir="auto"
          placeholder={t('crm.person.notePlaceholder')}
          value={body}
          onChange={(e) => setBody(e.target.value)}
        />
        <div style={{ marginTop: '0.5rem' }}>
          <button className="btn btn-sm" type="submit" disabled={add.isPending || !body.trim()}>
            {t('crm.person.addNote')}
          </button>
        </div>
      </form>
      <ul className="list-plain">
        {q.data?.items.map((n) => (
          <li key={n.id} style={{ display: 'block' }}>
            <div className="faint" style={{ fontSize: 'var(--t-xs)' }}>
              {n.authorName} · {fmt.dateTime(n.createdAt)}
            </div>
            <div dir="auto" style={{ whiteSpace: 'pre-wrap', fontSize: 'var(--t-sm)' }}>
              {n.body}
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}
